/**
 * ForceReloadButton (Plan 01-15, Task 4).
 *
 * Admin-only control mounted in the /settings/admin route. Triggers
 * POST /api/admin/force-reload (requires admin-scope per Plan 03 — D-04).
 * On success, the server broadcasts `system.reload` over the WS hub
 * (Plan 05) and every client running `useSystemReloadWS()` calls
 * `location.reload()` — including this admin's tab.
 *
 * UX (UI-SPEC §"Interaction Contracts §4 — Admin"):
 *   - Utløseren er en rad i Admin-lista (#159): «Tving oppdatering · Alle
 *     enheter»
 *   - Click opens a confirmation modal (role=dialog, aria-modal=true) so a
 *     mis-tap doesn't reload everyone's screens (T-dos-01 mitigation)
 *   - Bekreftknappen er `solid`, ikke `danger` (UI-SPEC §7: forstyrrende,
 *     men ikke ødeleggende)
 *   - On 403 the admin needs to re-enter the admin password (Plan 03 admin
 *     re-auth), so we surface t('pin.admin_password_wrong') inline
 *
 * Why the confirm modal stays open on 403: the admin needs to read the
 * error before deciding whether to navigate to admin re-auth. Auto-closing
 * on every non-200 buries the signal.
 */
import { createPortal } from 'react-dom';
import type { ReactElement } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '../../components/primitives';
import { ADMIN_ROW } from '../admin/adminStyles';

export function ForceReloadButton(): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const titleId = useId();

  async function onConfirm(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/force-reload', {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (res.ok) {
        setOpen(false);
        // The WS listener in App.tsx (useSystemReloadWS) will reload this
        // tab too — usually within a few hundred ms.
      } else if (res.status === 403 || res.status === 401) {
        setError(t('pin.admin_password_wrong'));
      } else {
        setError(t('error.offline'));
      }
    } catch {
      setError(t('error.offline'));
    } finally {
      setPending(false);
    }
  }

  function onCancel(): void {
    setOpen(false);
    setError(null);
  }

  return (
    <>
      {/* #159 (steg 9c): en rad i Admin-lista, «Tving oppdatering · Alle
          enheter». Raden åpner bekreftelsen direkte. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`${ADMIN_ROW} border-b border-card-line`}
        data-testid="force-reload-trigger"
      >
        <span className="min-w-0 flex-1">{t('admin.force_reload.cta')}</span>
        <span className="font-semibold text-ink-2">{t('admin.rows.force_reload_value')}</span>
        <Icon.ChevR size={18} className="shrink-0 text-ink-2" />
      </button>
      {/* Portal: glasskortet (backdrop-filter) ville ellers vært ramma for
          `fixed`, så dimmingen dekket bare kortet (#159). */}
      {open &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="fixed inset-0 z-50 flex items-center justify-center bg-dim p-4"
            data-testid="force-reload-modal"
          >
            <div className="w-full max-w-md space-y-4 rounded-lg bg-sheet p-6 shadow-lg">
              <h2 id={titleId} className="text-heading-s font-extrabold text-ink">
                {t('sw.force_reload.title')}
              </h2>
              <p className="text-sm text-ink-2">{t('sw.force_reload.body')}</p>
              {error !== null && (
                <p className="text-sm text-bad" role="alert" data-testid="force-reload-error">
                  {error}
                </p>
              )}
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={onCancel} data-testid="force-reload-cancel">
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="solid"
                  onClick={() => void onConfirm()}
                  disabled={pending}
                  data-testid="force-reload-confirm"
                >
                  {t('sw.force_reload.confirm')}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
